import type { CrmLeadRow } from '@/api/crm';

/**
 * RFC 4180 CSV: quoted fields, doubled quotes, line breaks inside quotes.
 * The separator is guessed from the header line (French Excel exports use
 * ";"). A UTF-8 byte-order mark is dropped.
 */
export const parseCsv = (input: string): string[][] => {
  const textIn = input.replace(/^﻿/, '');
  const firstLine = textIn.slice(0, textIn.search(/\r?\n|$/));
  const counts = [',', ';', '\t'].map(
    (sep) => [sep, firstLine.split(sep).length] as const
  );
  const sep = counts.sort((a, b) => b[1] - a[1])[0][0];
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < textIn.length; i++) {
    const c = textIn[i];
    if (quoted) {
      if (c === '"' && textIn[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"' && field === '') quoted = true;
    else if (c === sep) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && textIn[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
};

const normalize = (header: string) =>
  header
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Header names (EN/FR, Apollo, LinkedIn exports) for each lead field. */
const HEADERS: Record<keyof CrmLeadRow, string[]> = {
  firstName: ['first name', 'firstname', 'prenom', 'given name'],
  lastName: ['last name', 'lastname', 'nom', 'nom de famille', 'surname'],
  email: ['email', 'e mail', 'courriel', 'email address', 'work email'],
  phone: ['phone', 'telephone', 'tel', 'mobile', 'work direct phone', 'cell'],
  title: ['title', 'job title', 'poste', 'titre', 'fonction'],
  companyName: [
    'company',
    'company name',
    'entreprise',
    'organisation',
    'organization',
    'compagnie',
    'account name',
  ],
  website: ['website', 'site web', 'site', 'company website', 'url'],
  linkedinUrl: [
    'linkedin',
    'linkedin url',
    'person linkedin url',
    'profil linkedin',
  ],
  city: ['city', 'ville'],
  province: ['province', 'state', 'region'],
  country: ['country', 'pays'],
  estimatedEmployees: [
    'employees',
    'employes',
    'number of employees',
    'nombre d employes',
    'company size',
    'taille',
  ],
  notes: ['notes', 'note', 'comments', 'commentaires'],
};

/** Which column feeds which lead field; unknown columns are ignored. */
export const mapHeaders = (headers: string[]) => {
  const mapping: Partial<Record<keyof CrmLeadRow, number>> = {};
  headers.forEach((header, index) => {
    const name = normalize(header);
    const field = (Object.keys(HEADERS) as (keyof CrmLeadRow)[]).find(
      (key) => mapping[key] === undefined && HEADERS[key].includes(name)
    );
    if (field) mapping[field] = index;
  });
  return mapping;
};

/** Turns CSV rows (header first) into lead rows the API accepts. */
export const csvToLeads = (rows: string[][]) => {
  const [headers = [], ...data] = rows;
  const mapping = mapHeaders(headers);
  const leads = data.map((cells) => {
    const lead: CrmLeadRow = {};
    for (const [field, index] of Object.entries(mapping) as [
      keyof CrmLeadRow,
      number,
    ][]) {
      const value = (cells[index] ?? '').trim();
      if (!value) continue;
      if (field === 'estimatedEmployees') {
        const count = Number(value.replace(/[^\d]/g, ''));
        if (Number.isFinite(count) && value.match(/\d/))
          lead.estimatedEmployees = Math.min(count, 10000000);
      } else lead[field] = value;
    }
    return lead;
  });
  return { mapping, leads };
};
